import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetDashboard } from "../Dashboard/slice";

const GENERIC_ERROR_MESSAGE = "Incorrect email or password. Please try again.";

// Create async thunk for checking email verification status
export const checkEmailVerificationStatus = createAsyncThunk(
  "auth/checkEmailVerificationStatus",
  async (email, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL
        }/auth/cognito/email-verification-status/?email=${encodeURIComponent(
          email
        )}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(
          errorData.message || "Failed to check email verification status."
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to check email verification status."
      );
    }
  }
);

// Create async thunk for login
export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      // First, check email verification status
      const verificationResult = await dispatch(
        checkEmailVerificationStatus(email)
      );

      if (checkEmailVerificationStatus.rejected.match(verificationResult)) {
        return rejectWithValue(verificationResult.payload);
      }

      const verificationData = verificationResult.payload;

      // If email is not verified, return a special error to redirect to verify-email page
      if (!verificationData.email_verified) {
        return rejectWithValue({
          type: "EMAIL_NOT_VERIFIED",
          message: "Please verify your email before logging in.",
          email: email,
        });
      }

      // If email is verified, proceed with login
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/token/login/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || GENERIC_ERROR_MESSAGE);
      }

      const data = await response.json();
      const token = data.auth_token;
      localStorage.setItem("token", token);

      // Profile will be fetched automatically by AppContext when token changes
      return { token, user: { email } };
    } catch (error) {
      return rejectWithValue(error.message || GENERIC_ERROR_MESSAGE);
    }
  }
);

const initialState = {
  user: null,
  token: localStorage.getItem("token"),
  isLoading: false,
  error: null,
  isCheckingVerification: false,
  verificationError: null,
};

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.verificationError = null;
    },
    logoutSuccess: (state) => {
      state.user = null;
      state.token = null;
    },
    setMemberToken: (state, action) => {
      state.token = action.payload;
      state.user = null; // Will be fetched by AppContext
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check email verification status
      .addCase(checkEmailVerificationStatus.pending, (state) => {
        state.isCheckingVerification = true;
        state.verificationError = null;
      })
      .addCase(checkEmailVerificationStatus.fulfilled, (state) => {
        state.isCheckingVerification = false;
        state.verificationError = null;
      })
      .addCase(checkEmailVerificationStatus.rejected, (state, action) => {
        state.isCheckingVerification = false;
        state.verificationError = action.payload;
      })
      // Login user
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, logoutSuccess, setMemberToken } = loginSlice.actions;
export default loginSlice.reducer;

// Thunk action creator for logout
export const logout = () => (dispatch) => {
  localStorage.removeItem("token");
  dispatch(resetDashboard());
  dispatch(logoutSuccess());
};
